import React, { useMemo } from 'react'
import { Dropdown } from 'react-bootstrap'
import { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { MIME_TYPE } from 'src/Utils/globalEnums'
import { useGooglePicker } from 'src/lib/Linkers/modals/Google Drive Wizard/GoogleDriveWizard.hooks'
import { useGoogleDriveContext } from 'src/lib/Linkers/context/GoogleDriveContext'
import { FileSelectorText } from 'src/components/Reusable/Forms/FileSelector/FileSelector.styles'
import { useSetActiveModal } from 'src/Modal/ModalStateContext'
import { ModalFormId } from 'src/components/ModalForms'
import { useSharepointFilePickerContext } from 'src/lib/Linkers/modals/SharePoint Picker/SharepointFilePickerContext'
import { ExtendedDocumentObject } from 'src/lib/Linkers/Linkers.constants'
import { FlexRow, FlexAlign } from 'src/components/Reusable/Flex'
import { useSharepointLinkPaste } from 'src/context/Gatekeeper'
import { useConfluenceFilePickerContext } from 'src/lib/Linkers/modals/Confluence Picker/ConfluenceFilePickerContext'
import {
  useHasReceptor,
  useReceptors,
} from 'src/pages/Receptors/receptors.hooks'
import { documentSourceLabel } from 'src/pages/Receptors/documentSourceLabel.helpers'
import { ReactComponent as LinkIcon } from '../../../components/Icons/assets/permalink-icon.svg'
import { TypeButton } from '../FileType/TypeSelector.styles'
import { DocumentFormData, DocumentType } from './AddDocumentForm'
import {
  DMTitle,
  DMSize,
  GoogleDriveIcon,
  GoogleDriveSmallIcon,
  SharePointIcon,
  SharePointSmallIcon,
  DocumentManagementFilesContainer,
  ConfluenceIcon,
  LinkerCard,
  SelectionCardWrapper,
} from './AddDocumentForm.styles'

export const PolicyDocLinkTypeButton = ({
  setFormData,
  setType,
}: {
  setFormData: React.Dispatch<React.SetStateAction<DocumentFormData>>
  setType: React.Dispatch<React.SetStateAction<DocumentType>>
}): JSX.Element => {
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setFormData((state) => ({ ...state, mime: MIME_TYPE.TEXT_URI_LIST }))
    setType(DocumentType.LINK)
  }

  return (
    <TypeButton onClick={onClick}>
      <LinkIcon width="48px" height="48px" />
      <FileSelectorText>Paste Link</FileSelectorText>
    </TypeButton>
  )
}

export const PolicyDocGoogleDriveButton = ({
  policyId,
  setGdriveError,
}: {
  policyId: string
  setGdriveError: React.Dispatch<React.SetStateAction<string>>
}): JSX.Element => {
  const openPicker = useGooglePicker()
  const { setPickerConfig, setSubjectIds, setSource } = useGoogleDriveContext()

  const { data: HasGDriveReceptor } = useHasReceptor('trr-gdrive', true)
  const hasGDriveReceptor = useMemo(
    () => HasGDriveReceptor?.getHasReceptor() || false,
    [HasGDriveReceptor],
  )
  const { data: gdriveReceptors } = useReceptors(
    'trr-gdrive',
    hasGDriveReceptor,
  )
  const enabledGDriveReceptors = (
    gdriveReceptors?.getReceptorsList() || []
  ).filter((f) => f.getIsenabled())

  const handleGoogleDriveSelection = async (receptor: ReceptorRecord) => {
    setSubjectIds([policyId])
    setSource('Policy')
    setPickerConfig({
      allowMultiselect: true,
      modalId: ModalFormId.ADD_DOCUMENT,
    })
    const config = {
      allowMultiselect: true,
      modalId: ModalFormId.ADD_DOCUMENT,
    }
    // The document picker becomes the active modal and re-opens this one
    // when it closes; hiding here would close the picker as well.
    const { invalidCredentials, exceptions } = await openPicker(
      config,
      receptor.toObject(),
    )
    if (exceptions) {
      setGdriveError(exceptions)
      setPickerConfig(null)
    } else if (invalidCredentials) {
      setGdriveError('Invalid Credentials.')
      setPickerConfig(null)
    }
  }

  if (enabledGDriveReceptors.length > 1) {
    return (
      <SelectionCardWrapper
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        key="gdrive-dropdown"
      >
        <Dropdown
          style={{
            height: '100%',
            display: 'flex',
            flexGrow: 1,
            width: '100%',
          }}
        >
          <Dropdown.Toggle as={LinkerCard} id="dropdown-basic">
            <GoogleDriveIcon />
            <FileSelectorText>Google Drive</FileSelectorText>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {enabledGDriveReceptors.map((receptor) => (
              <Dropdown.Item
                key={receptor.getId()}
                onClick={async () => {
                  handleGoogleDriveSelection(receptor)
                }}
              >
                {documentSourceLabel(receptor)}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </SelectionCardWrapper>
    )
  }

  return (
    <TypeButton
      onClick={async (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (enabledGDriveReceptors.length > 0) {
          handleGoogleDriveSelection(enabledGDriveReceptors[0])
        }
      }}
    >
      <GoogleDriveIcon />
      <FileSelectorText>Google Drive</FileSelectorText>
    </TypeButton>
  )
}

export const PolicyDocSharepointButton = ({
  setFormData,
  setType,
}: {
  setFormData: React.Dispatch<React.SetStateAction<DocumentFormData>>
  setType: React.Dispatch<React.SetStateAction<DocumentType>>
}): JSX.Element => {
  const openModal = useSetActiveModal(ModalFormId.SHAREPOINT_FILE_PICKER)
  const useSPLinkPaste = useSharepointLinkPaste()
  const { setReceptor: setSharepointReceptor } =
    useSharepointFilePickerContext()

  const { data: HasSharepointReceptor } = useHasReceptor('trr-sharepoint', true)
  const hasSharepointReceptor = useMemo(
    () => HasSharepointReceptor?.getHasReceptor() || false,
    [HasSharepointReceptor],
  )
  const { data: spReceptors } = useReceptors(
    'trr-sharepoint',
    hasSharepointReceptor,
  )
  const enabledSharepointReceptors = (
    spReceptors?.getReceptorsList() || []
  ).filter((f) => f.getIsenabled())

  const handleSharepointSelection = (receptor: ReceptorRecord) => {
    if (useSPLinkPaste) {
      setFormData((state) => ({ ...state, mime: MIME_TYPE.TEXT_URI_LIST }))
      setType(DocumentType.SHAREPOINTLINK)
    } else {
      setSharepointReceptor(receptor)
      openModal()
    }
  }

  if (enabledSharepointReceptors.length > 1) {
    return (
      <SelectionCardWrapper
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        key="sharepoint-dropdown"
      >
        <Dropdown
          style={{
            height: '100%',
            display: 'flex',
            flexGrow: 1,
            width: '100%',
          }}
        >
          <Dropdown.Toggle as={LinkerCard} id="dropdown-basic">
            <SharePointIcon />
            <FileSelectorText>Sharepoint</FileSelectorText>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {enabledSharepointReceptors.map((receptor) => (
              <Dropdown.Item
                key={receptor.getId()}
                onClick={() => {
                  handleSharepointSelection(receptor)
                }}
              >
                {receptor.getTenantid()}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </SelectionCardWrapper>
    )
  }

  return (
    <TypeButton
      onClick={(e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (enabledSharepointReceptors.length > 0) {
          handleSharepointSelection(enabledSharepointReceptors[0])
        }
      }}
    >
      <SharePointIcon />
      <FileSelectorText>Sharepoint</FileSelectorText>
    </TypeButton>
  )
}

export const PolicyDocConfluenceButton = ({
  policyId,
  setFormData,
  setType,
}: {
  policyId: string
  setFormData?: React.Dispatch<React.SetStateAction<DocumentFormData>>
  setType?: React.Dispatch<React.SetStateAction<DocumentType>>
}): JSX.Element => {
  const openModal = useSetActiveModal(ModalFormId.CONFLUENCE_FILE_PICKER)
  const {
    setSubjectIds,
    setSource,
    setReceptor: setConfluenceReceptor,
  } = useConfluenceFilePickerContext()

  const { data: HasConfluenceReceptor } = useHasReceptor(
    'trr-confluence-dms',
    true,
  )

  const hasConfluenceReceptor = useMemo(
    () => HasConfluenceReceptor?.getHasReceptor() || false,
    [HasConfluenceReceptor],
  )

  const { data: confluenceReceptors } = useReceptors(
    'trr-confluence-dms',
    hasConfluenceReceptor,
  )

  const enabledConfluenceReceptors = (
    confluenceReceptors?.getReceptorsList() || []
  ).filter((f) => f.getIsenabled())

  const handleConfluenceSelection = (receptor: ReceptorRecord) => {
    // Align with Google Drive pattern: configure context before opening modal
    setSubjectIds([policyId])
    setSource('Policy')
    setConfluenceReceptor(receptor)

    // We currently don't need to touch formData/type for Confluence,
    // since linking is handled entirely via the linker context.
    // setFormData and setType are accepted for API symmetry but unused.
    void setFormData
    void setType

    openModal()
  }

  if (enabledConfluenceReceptors.length > 1) {
    return (
      <SelectionCardWrapper
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        key="confluence-dropdown"
      >
        <Dropdown
          style={{
            height: '100%',
            display: 'flex',
            flexGrow: 1,
            width: '100%',
          }}
        >
          <Dropdown.Toggle as={LinkerCard} id="dropdown-basic">
            <ConfluenceIcon />
            <FileSelectorText>Confluence</FileSelectorText>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {enabledConfluenceReceptors.map((receptor) => (
              <Dropdown.Item
                key={receptor.getId()}
                onClick={() => {
                  handleConfluenceSelection(receptor)
                }}
              >
                {receptor.getTenantid()}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </SelectionCardWrapper>
    )
  }

  return (
    <TypeButton
      onClick={(e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (enabledConfluenceReceptors.length > 0) {
          handleConfluenceSelection(enabledConfluenceReceptors[0])
        }
      }}
    >
      <ConfluenceIcon />
      <FileSelectorText>Confluence</FileSelectorText>
    </TypeButton>
  )
}

export const PolicyDocumentManagementFiles = (): JSX.Element => {
  const { filesSelected: spFileSelected } = useSharepointFilePickerContext()
  const { filesSelected: gdFileSelected } = useGoogleDriveContext()
  const { filesSelected: cfFileSelected } = useConfluenceFilePickerContext()

  const spFiles = spFileSelected ?? []
  const gdFiles: ExtendedDocumentObject[] = gdFileSelected ?? []
  const cfFiles = cfFileSelected ?? []

  if (!spFiles.length && !gdFiles.length && !cfFiles.length) {
    return <></>
  }

  return (
    <DocumentManagementFilesContainer>
      {spFiles.map((file) => (
        <FlexRow
          key={file.id}
          justify={FlexAlign.FLEX_START}
          align={FlexAlign.CENTER}
          $padding={12}
          gap={8}
          isFullWidth
        >
          <SharePointSmallIcon />
          <DMTitle>{file.name}</DMTitle>
          {file.size && <DMSize>{file.size} bytes</DMSize>}
        </FlexRow>
      ))}
      {gdFiles.map((file) => (
        <FlexRow
          key={file.id}
          justify={FlexAlign.FLEX_START}
          align={FlexAlign.CENTER}
          $padding={12}
          gap={8}
          isFullWidth
        >
          <GoogleDriveSmallIcon />
          <DMTitle>{file.name}</DMTitle>
          {file.sizeBytes && <DMSize>{file.sizeBytes} bytes</DMSize>}
        </FlexRow>
      ))}
      {cfFiles.map((node) => (
        <FlexRow
          key={node.id}
          justify={FlexAlign.FLEX_START}
          align={FlexAlign.CENTER}
          $padding={12}
          gap={8}
          isFullWidth
        >
          <ConfluenceIcon />
          <DMTitle>{node.title ?? node.fileName ?? node.id}</DMTitle>
        </FlexRow>
      ))}
    </DocumentManagementFilesContainer>
  )
}
